import React, { useContext, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { EventAttendee } from 'app/shared/typings';
import { AlertContext } from 'app/shared/context';
import { AuthContext } from 'app/shared/context/Auth';
import { UpdateEventAttendeeVisibleToUser } from 'app/fan/graphql/eventAttendees/mutationHooks';
import { useEventPageContext } from 'app/fan/context/EventPage';
import { ExpressCheckoutContextProvider } from 'app/fan/context/ExpressCheckout';
import { StripeElementsProvider } from 'app/fan/context/StripeElementsProvider';
import {
  EventPageLoadingBlocks,
  GuestShareLinkModal,
} from 'app/fan/components/pages/Event/components';
import {
  BuyFlowLoggedInPostPurchaseContainer,
  BuyFlowPrePurchaseContainer,
} from 'app/fan/components/pages/Event/EventPages';

export const BuyFlowAddToAccountEventPage = () => {
  const { formatMessage } = useIntl();
  const { event, token } = useEventPageContext();
  const { user: loggedInUser } = useContext(AuthContext);

  const [eventAttendee, setEventAttendee] = useState<EventAttendee | null>(
    null
  );

  const [
    updateEventAttendeeVisibleToUser,
    {
      data: eventAttendeeData,
      loading: eventAtendeeUpdating,
      error: eventAttendeeUpdateError,
    },
  ] = UpdateEventAttendeeVisibleToUser({
    variables: { eventId: event?.id, token, visibleToUser: true },
  });

  useEffect(() => {
    if (
      !eventAttendeeData &&
      !eventAtendeeUpdating &&
      !eventAttendeeUpdateError
    ) {
      updateEventAttendeeVisibleToUser();
    }
  }, [eventAttendeeData, eventAtendeeUpdating, eventAttendeeUpdateError]);

  useEffect(() => {
    if (eventAttendeeData && !eventAttendee) {
      setEventAttendee(
        eventAttendeeData.updateEventAttendeeVisibleToUser.eventAttendee
      );
    }
  }, [eventAttendeeData, eventAttendee]);

  const [showShareLinkModal, setShowShareLinkModal] = useState(false);
  useEffect(() => {
    if (event && loggedInUser && eventAttendee) {
      setTimeout(() => {
        setShowShareLinkModal(true);
      }, 3000);
    }
  }, [event, loggedInUser, eventAttendee]);

  const alertContext = useContext(AlertContext);

  useEffect(() => {
    if (!loggedInUser || !!eventAttendeeUpdateError) {
      alertContext.addAlert({
        message: formatMessage({ id: 'event.addToAccountFlow.error' }),
        type: 'error',
      });
    } else if (eventAttendee) {
      alertContext.addAlert({
        message: formatMessage({ id: 'event.addToAccountFlow.success' }),
        type: 'success',
        id: 'event-page-buy-flow-add-to-account-success',
      });
    }

    return () => {
      alertContext.clearAlerts();
    };
  }, [eventAttendee, loggedInUser, eventAttendeeUpdateError]);

  if (eventAtendeeUpdating) {
    return <EventPageLoadingBlocks />;
  }

  // We will never run into this scenario, but include this code so that
  // Typescript knows to expect this to be present
  if (!event) {
    return null;
  }

  if (loggedInUser && !eventAttendeeUpdateError && eventAttendee) {
    return (
      <>
        <StripeElementsProvider eventId={event.id}>
          <ExpressCheckoutContextProvider
            event={event}
            eventAttendee={eventAttendee}
          >
            <BuyFlowLoggedInPostPurchaseContainer
              data-qaid="event-page-buy-flow-add-to-account"
              eventAttendee={eventAttendee}
            />
          </ExpressCheckoutContextProvider>
        </StripeElementsProvider>

        <GuestShareLinkModal
          isOpen={showShareLinkModal}
          setIsOpen={setShowShareLinkModal}
          event={event}
          ticketPurchaserName={loggedInUser.firstName}
          ticketPurchaserEventAttendeeId={eventAttendee.id}
        />
      </>
    );
  }

  const qaId =
    !loggedInUser || !!eventAttendeeUpdateError
      ? 'buy-flow-add-to-account-error'
      : undefined;

  return (
    <StripeElementsProvider eventId={event.id}>
      <ExpressCheckoutContextProvider
        event={event}
        eventAttendee={eventAttendee || undefined}
      >
        <BuyFlowPrePurchaseContainer data-qaid={qaId} />
      </ExpressCheckoutContextProvider>
    </StripeElementsProvider>
  );
};
